<!-- src/lib/components/migration/MappingConfirm.svelte -->
<!-- Step 5: Review and edit status mappings, then confirm to start import -->
<script lang="ts">
  import { api } from '../../api.ts';

  interface Props {
    runId: string;
    onNext: (statusMap: Record<string, string>) => void;
    onBack: () => void;
  }
  let { runId, onNext, onBack }: Props = $props();

  // Default status mapping: source → agentboard
  let statusMap = $state<Record<string, string>>({
    backlog:     'todo',
    todo:        'todo',
    in_progress: 'in_progress',
    done:        'done',
    cancelled:   'cancelled',
  });

  const agentboardStatuses = ['todo', 'in_progress', 'done', 'cancelled', 'blocked'];

  let confirming = $state(false);
  let error = $state<string | null>(null);

  const sourceStatuses = $derived(Object.keys(statusMap));

  async function confirmMapping() {
    confirming = true;
    error = null;
    try {
      await api<{ ok: true }>('/api/migrate/confirm-mapping', {
        method: 'POST',
        body: JSON.stringify({ run_id: runId, status_map: statusMap }),
      });
      onNext(statusMap);
    } catch (e) {
      error = e instanceof Error ? e.message : 'Failed to confirm mapping.';
      confirming = false;
    }
  }
</script>

<div class="step">
  <h2 class="step-title">Review status mapping</h2>
  <p class="step-hint">Map your source statuses to agentboard statuses. Adjust as needed.</p>

  <div class="mapping-table">
    <div class="table-header">
      <span>Source status</span>
      <span>Agentboard status</span>
    </div>
    {#each sourceStatuses as srcStatus}
      <div class="table-row">
        <span class="src-status">{srcStatus}</span>
        <select
          class="status-select"
          bind:value={statusMap[srcStatus]}
          disabled={confirming}
        >
          {#each agentboardStatuses as s}
            <option value={s}>{s}</option>
          {/each}
        </select>
      </div>
    {/each}
  </div>

  {#if error}
    <p class="error">{error}</p>
  {/if}

  <div class="actions">
    <button class="btn-secondary" onclick={onBack} disabled={confirming}>Back</button>
    <button class="btn-primary" onclick={confirmMapping} disabled={confirming}>
      {#if confirming}
        <span class="spinner" aria-hidden="true"></span> Starting…
      {:else}
        Start Import
      {/if}
    </button>
  </div>
</div>

<style>
.step { display: flex; flex-direction: column; gap: 1rem; }
.step-title { margin: 0; font-size: 1.05rem; font-weight: 600; color: var(--text-primary); }
.step-hint { margin: 0; font-size: 0.82rem; color: var(--text-muted); }

.mapping-table {
  display: flex; flex-direction: column;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  overflow: hidden;
}

.table-header {
  display: grid; grid-template-columns: 1fr 1fr;
  padding: 0.5rem 0.875rem;
  background: rgba(255,255,255,0.04);
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.table-row {
  display: grid; grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 0.5rem 0.875rem;
  border-top: 1px solid rgba(255,255,255,0.04);
  gap: 0.75rem;
}
.table-row:hover { background: rgba(255,255,255,0.02); }

.src-status {
  font-size: 0.82rem;
  color: var(--text-secondary);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.76rem;
}

.status-select {
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 4px;
  color: var(--text-primary);
  font-size: 0.8rem;
  padding: 0.3rem 0.5rem;
  cursor: pointer;
  outline: none;
  transition: border-color 120ms ease;
}
.status-select:focus { border-color: var(--ab-purple-core); }
.status-select:disabled { opacity: 0.5; cursor: not-allowed; }

.error { margin: 0; font-size: 0.78rem; color: var(--ab-red-bright); }

.actions { display: flex; gap: 0.5rem; justify-content: flex-end; }

.btn-primary {
  display: flex; align-items: center; gap: 0.4rem;
  background: oklch(0.72 0.28 310 / 0.12);
  border: 1px solid oklch(0.72 0.28 310 / 0.32);
  color: var(--ab-purple-bright);
  padding: 0.4rem 1.25rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 120ms ease;
}
.btn-primary:hover:not(:disabled) { background: oklch(0.72 0.28 310 / 0.22); }
.btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }

.btn-secondary {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  color: var(--text-muted);
  padding: 0.4rem 0.875rem;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
  transition: background 120ms ease;
}
.btn-secondary:hover:not(:disabled) { background: rgba(255,255,255,0.07); }
.btn-secondary:disabled { opacity: 0.4; cursor: not-allowed; }

.spinner {
  display: inline-block;
  width: 12px; height: 12px;
  border: 2px solid rgba(255,255,255,0.2);
  border-top-color: var(--ab-purple-bright);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
